import { cn } from "@newxsystems/shared";
import { TootActions } from "../../../../modules/layout/components/TootActions/TootActions";
import { TootHeading } from "../../../../modules/layout/components/TootHeading/TootHeading";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import { TootMapPanel } from "../../../../modules/contact/components/TootMapPanel/TootMapPanel";
import type {
  BlockAddress,
  BlockEmbed,
  IContactMapEmbedBlockProps,
} from "./ContactMapEmbedBlock.props";

const getAddressLines = (address?: BlockAddress | null) => {
  if (!address) {
    return [];
  }

  const regionLine = [address.region, address.postalCode].filter(Boolean).join(" ");
  const localityLine = [address.city, regionLine].filter(Boolean).join(", ");

  return [address.line1, address.line2, localityLine, address.country].filter(Boolean) as string[];
};

const getLocationLabel = (address?: BlockAddress | null) => {
  const locality = [address?.city, address?.region].filter(Boolean).join(" / ");
  return locality || address?.country || undefined;
};

const createMapEmbed = (mapEmbed?: BlockEmbed | null) => {
  if (!mapEmbed) {
    return undefined;
  }

  const { src, title } = mapEmbed;

  return (
    <iframe
      className="absolute inset-0 block h-full w-full border-0"
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      src={src}
      title={title}
    />
  );
};

const ContactMapAddressPanel = ({
  addressLines,
  locationLabel,
}: {
  addressLines: string[];
  locationLabel?: string;
}) => {
  if (!locationLabel && addressLines.length === 0) {
    return null;
  }

  return (
    <article className="toot-heat-panel p-5 md:p-6">
      <div className="relative space-y-4">
        {locationLabel ? (
          <h3 className="m-0 text-pretty text-2xl font-black leading-tight tracking-[-0.045em] text-[var(--color-toot-foreground)]">
            {locationLabel}
          </h3>
        ) : null}

        {addressLines.length > 0 ? (
          <address className="not-italic text-sm leading-7 text-[color-mix(in_oklab,var(--color-toot-foreground)_82%,transparent)]">
            {addressLines.map((line, index) => (
              <span className="block [overflow-wrap:anywhere]" key={`${line}-${index}`}>
                {line}
              </span>
            ))}
          </address>
        ) : null}
      </div>
    </article>
  );
};

export const ContactMapEmbedBlock = ({
  address,
  anchorId,
  buttons,
  eyebrow,
  fallbackDirectionsLink,
  heading,
  mapEmbed,
  parentClass,
  spacing,
  subheading,
}: IContactMapEmbedBlockProps) => {
  const addressLines = getAddressLines(address);
  const addressSummary = addressLines.join(" · ");
  const locationLabel = getLocationLabel(address);
  const providerLabel = mapEmbed?.provider;
  const mapPanelEmbed = createMapEmbed(mapEmbed);
  const actions = buttons ?? [];
  const hasHeader = Boolean(heading || eyebrow || subheading);
  const hasSideContent = Boolean(
    locationLabel || addressLines.length > 0 || fallbackDirectionsLink || actions.length > 0,
  );

  if (!mapPanelEmbed && !hasHeader && !hasSideContent) {
    return null;
  }

  return (
    <TootSectionShell
      className={cn(
        "toot-heat-panel grid gap-5 p-4 md:p-5",
        hasSideContent && "lg:grid-cols-[minmax(0,1.42fr)_minmax(18rem,0.58fr)] lg:items-stretch",
      )}
      containerClassName="relative"
      header={
        hasHeader ? (
          <TootHeading
            alignment="left"
            eyebrow={eyebrow}
            heading={heading}
            subheading={subheading}
          />
        ) : undefined
      }
      id={anchorId ?? undefined}
      rootClassName={parentClass}
      spacing={spacing ?? undefined}
      tone="default"
      width="wide"
    >
      <TootMapPanel
        addressSummary={addressSummary}
        className="min-h-[34rem] lg:min-h-[40rem]"
        directionsAriaLabel={fallbackDirectionsLink?.ariaLabel ?? undefined}
        directionsHref={fallbackDirectionsLink?.href ?? undefined}
        directionsLabel={fallbackDirectionsLink?.text}
        directionsTarget={fallbackDirectionsLink?.target ?? undefined}
        directionsTitle={fallbackDirectionsLink?.ariaLabel ?? undefined}
        embed={mapPanelEmbed}
        providerLabel={providerLabel}
        title={mapEmbed?.title ?? undefined}
      />

      {hasSideContent ? (
        <div className="flex min-w-0 flex-col gap-4">
          <ContactMapAddressPanel addressLines={addressLines} locationLabel={locationLabel} />
          {actions.length > 0 ? <TootActions actions={actions} alignment="left" /> : null}
        </div>
      ) : null}
    </TootSectionShell>
  );
};

ContactMapEmbedBlock.displayName = "Block::ContactMapEmbedBlock";
